import React from 'react';
import { FileText } from 'lucide-react';
import { LEGAL_EFFECTIVE_DATE_DISPLAY } from '../utils/legalVersion';

// Legal content reflecting what QuoteHaul actually does today (Firebase
// Auth/Firestore storage, Stripe billing, no-refund cancellation policy).
export default function TermsOfServicePage({ onOpenPrivacy }) {
  return (
    <div className="space-y-5 text-sm text-slate-300 leading-relaxed">
      <div className="flex items-center gap-2.5">
        <FileText className="w-5 h-5 text-blue-400 shrink-0" />
        <h1 className="text-xl font-bold text-white">Terms of Service</h1>
      </div>
      <p className="text-xs text-slate-500">Last updated: {LEGAL_EFFECTIVE_DATE_DISPLAY}</p>

      <section className="space-y-2">
        <h2 className="text-white font-bold">1. Agreement to Terms</h2>
        <p>
          These Terms of Service ("Terms") govern access to and use of QuoteHaul (the "Service"), operated by
          XAYVR Company, LLC, a Georgia limited liability company ("QuoteHaul," "we," "us," or "our"). By creating
          an account, or by using the Service in
          guest mode without an account, you agree to be bound by these Terms. If you do not agree, do not use
          the Service.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">2. Who May Use QuoteHaul</h2>
        <p>
          QuoteHaul is intended for business use by freight brokers, 3PLs, freight forwarders, shippers, and
          carriers, and by individuals
          at least 18 years old who are authorized to act on behalf of the company they register. By creating an
          account, you represent that the company information you provide is accurate and that you're authorized
          to bind that company to these Terms.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">3. Accounts and Organizations</h2>
        <p>
          Creating an account establishes an organization ("tenant") that you administer. You may invite
          teammates as additional seats under your organization; as the organization's admin, you're responsible
          for the actions of members you invite and for keeping their access appropriate (including revoking
          access when someone leaves). You're responsible for maintaining the confidentiality of your account
          credentials and for all activity under your account.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">4. Subscription, Billing, and Cancellation</h2>
        <p>
          QuoteHaul offers a Free tier and a paid "Pro" tier billed through Stripe, our third-party payment
          processor. Pro subscriptions may include a free trial period as stated at checkout; unless cancelled
          before the trial ends, the subscription automatically renews at the price shown at signup.
        </p>
        <p>
          <strong className="text-slate-200">No refunds.</strong> Payments are non-refundable. If you cancel a
          paid subscription, you retain Pro access through the remainder of the billing period you already paid
          for; it will not renew afterward. An organization admin can cancel or downgrade a subscription from
          Settings, or by contacting us.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">5. Your Data and Content</h2>
        <p>
          You retain ownership of the quotes, shipment records, organization/contact information, rate cards, and
          documents you or your team upload or create in QuoteHaul ("Customer Data"). You grant us a limited
          license to host, store, and process Customer Data solely to provide and maintain the Service. See our{' '}
          <button type="button" onClick={() => onOpenPrivacy && onOpenPrivacy()} className="text-blue-400 hover:underline font-semibold">
            Privacy Policy
          </button>
          {' '}for details on how this data is collected, stored, and used.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">6. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Use the Service for any unlawful purpose, or to submit fraudulent freight quotes, shipments, or billing information.</li>
          <li>Attempt to access another organization's data, or circumvent the Service's access controls.</li>
          <li>Reverse-engineer, resell, or white-label the Service without our written permission.</li>
          <li>Upload malicious code or attempt to disrupt the Service's availability.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">7. Service Availability</h2>
        <p>
          QuoteHaul is provided on an "as is" and "as available" basis. We aim for reliable uptime but do not
          guarantee the Service will be uninterrupted, error-free, or available at all times, and we may modify
          or discontinue features with reasonable notice where practical.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">8. Disclaimer of Warranties; Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, QuoteHaul disclaims all warranties, express or implied,
          including merchantability, fitness for a particular purpose, and non-infringement. QuoteHaul is a
          quoting and record-keeping tool — it does not independently verify carrier authority, insurance, rates,
          or shipment accuracy, and you remain responsible for verifying such information before relying on it.
          To the fullest extent permitted by law, QuoteHaul's total liability arising out of or relating to the
          Service will not exceed the amount you paid us in the twelve (12) months preceding the claim.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">9. Termination</h2>
        <p>
          We may suspend or terminate access to the Service for a violation of these Terms. You may stop using
          the Service, and an organization admin may request deletion of the organization's account and data, at
          any time by contacting us (see Section 11).
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">10. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Georgia, without regard to conflict-of-law
          principles.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-bold">11. Contact</h2>
        <p>
          Questions about these Terms can be sent to{' '}
          <a href="mailto:support@quotehaul.app" className="text-blue-400 hover:underline">support@quotehaul.app</a>.
        </p>
      </section>
    </div>
  );
}
